const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface ApiError {
  error?: string;
  detail?: string;
  [key: string]: any;
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  // Get CSRF token from cookie
  private getCsrfToken(): string | null {
    const name = 'csrftoken';
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.startsWith(name + '=')) {
        return trimmedCookie.substring(name.length + 1);
      }
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get CSRF token and add to headers for non-GET requests
    const csrfToken = this.getCsrfToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add existing headers
    if (options.headers) {
      const existingHeaders = options.headers as Record<string, string>;
      Object.assign(headers, existingHeaders);
    }

    // Add CSRF token for non-GET requests
    if (csrfToken && options.method && options.method !== 'GET') {
      headers['X-CSRFToken'] = csrfToken;
    }

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'include', // Important for session cookies
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const error: ApiError = await response.json().catch(() => ({
          error: `HTTP ${response.status}: ${response.statusText}`
        }));

        // Handle Django validation errors format
        if (typeof error === 'object' && !error.error && !error.detail) {
          // Convert field errors to readable message
          const fieldErrors = Object.entries(error)
            .map(([field, messages]) => {
              const msgArray = Array.isArray(messages) ? messages : [messages];
              return `${field}: ${msgArray.join(', ')}`;
            })
            .join('; ');
          throw new Error(fieldErrors || 'Validation error');
        }

        throw new Error(error.error || error.detail || 'An error occurred');
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return {} as T;
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  private async requestAllPages<T>(endpoint: string): Promise<T[]> {
    let page = 1;
    const allItems: T[] = [];

    while (true) {
      const separator = endpoint.includes('?') ? '&' : '?';
      const response = await this.request<any>(`${endpoint}${separator}page=${page}`);

      if (Array.isArray(response)) {
        return response as T[];
      }

      const items = Array.isArray(response?.results) ? response.results as T[] : [];
      allItems.push(...items);

      if (!response?.next) {
        break;
      }

      page += 1;
    }

    return allItems;
  }

  // Auth endpoints
  async getCsrfTokenFromServer() {
    // Fetch CSRF token from Django
    await fetch(`${this.baseUrl}/users/csrf/`, {
      credentials: 'include',
    }).catch(() => {
      // Ignore errors - this is just to get the CSRF cookie set
    });
  }

  async login(email: string, password: string) {
    // Ensure CSRF token is set before login
    await this.getCsrfTokenFromServer();
    return this.request<any>('/users/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout() {
    return this.request<{ message: string }>('/users/logout/', {
      method: 'POST',
    });
  }

  async register(data: {
    username: string;
    email: string;
    password: string;
    password_confirm: string;
    user_type: string;
    first_name?: string;
    last_name?: string;
    company_name?: string;
  }) {
    // Ensure CSRF token is set before registration
    await this.getCsrfTokenFromServer();
    return this.request<any>('/users/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async registerWithFile(formData: FormData) {
    // For file uploads, we need to handle this separately
    const csrfToken = this.getCsrfToken() || (await this.getCsrfTokenFromServer());

    const response = await fetch(`${this.baseUrl}/users/`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrfToken || '',
      },
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));

      if (typeof error === 'object' && !error.error && !error.detail) {
        const fieldErrors = Object.entries(error)
          .map(([field, messages]) => {
            const msgArray = Array.isArray(messages) ? messages : [messages];
            return `${field}: ${msgArray.join(', ')}`;
          })
          .join('; ');
        throw new Error(fieldErrors || 'Validation error');
      }

      throw new Error(error.error || error.detail || 'Registration failed');
    }

    return response.json();
  }

  async getCurrentUser() {
    return this.request<any>('/users/me/');
  }

  async getUsers(params?: Record<string, string>) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any>(`/users/${queryString}`);
  }

  async getUser(id: number) {
    return this.request<any>(`/users/${id}/`);
  }

  async getPhoneDuplicates(params?: Record<string, string>) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any>(`/users/phone_duplicates/${queryString}`);
  }

  async validatePhoneDuplicate(phone_number: string) {
    return this.request<any>('/users/phone-duplicates/validate/', {
      method: 'POST',
      body: JSON.stringify({ phone_number }),
    });
  }

  async getDniDuplicates(params?: Record<string, string>) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any>(`/users/dni_duplicates/${queryString}`);
  }

  async validateDniDuplicate(dni: string) {
    return this.request<any>('/users/dni-duplicates/validate/', {
      method: 'POST',
      body: JSON.stringify({ dni }),
    });
  }

  async updateUser(id: number, data: any) {
    return this.request<any>(`/users/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async changeMyPassword(data: {
    old_password: string;
    new_password: string;
    new_password_confirm: string;
  }) {
    return this.request<{ message: string }>('/users/change_password/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resetUserPassword(id: number, data: { new_password: string; new_password_confirm: string }) {
    return this.request<{ message: string }>(`/users/${id}/reset_password/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Jobs endpoints
  async getJobs(params?: Record<string, string>) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any>(`/jobs/${queryString}`);
  }

  async getJob(id: number) {
    return this.request<any>(`/jobs/${id}/`);
  }

  async getMyJobs() {
    return this.requestAllPages<any>('/jobs/my_jobs/');
  }

  async getJobsRecap(params?: Record<string, string>) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any>(`/jobs/recap/${queryString}`);
  }

  async createJob(data: any) {
    return this.request<any>('/jobs/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateJob(id: number, data: any) {
    return this.request<any>(`/jobs/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteJob(id: number) {
    return this.request<void>(`/jobs/${id}/`, {
      method: 'DELETE',
    });
  }

  async reactivateJob(id: number) {
    return this.request<any>(`/jobs/${id}/reactivate/`, {
      method: 'POST',
    });
  }

  async applyToJob(id: number, formData: FormData) {
    const csrfToken = this.getCsrfToken() || (await this.getCsrfTokenFromServer());

    // For FormData, we don't set Content-Type; browser sets it automatically with boundary
    const response = await fetch(`${this.baseUrl}/jobs/${id}/apply/`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrfToken || '',
      },
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));

      if (typeof error === 'object' && !error.error && !error.detail) {
        const fieldErrors = Object.entries(error)
          .map(([field, messages]) => {
            const msgArray = Array.isArray(messages) ? messages : [messages];
            return `${field}: ${msgArray.join(', ')}`;
          })
          .join('; ');
        throw new Error(fieldErrors || 'Failed to apply');
      }

      throw new Error(error.error || error.detail || 'Failed to apply');
    }

    return response.json();
  }

  async getMyApplications() {
    return this.requestAllPages<any>('/applications/my_applications/');
  }

  async getApplicationsAsJobPoster() {
    // For job posters, get all applications to their jobs (uses backend get_queryset filtering)
    return this.request<any>('/applications/');
  }

  async getJobApplications(jobId: number) {
    // Get applications for a specific job (requires auth, only job owner/admin/moderator can access)
    return this.requestAllPages<any>(`/jobs/${jobId}/applications/`);
  }

  async downloadJobApplicationsZip(jobId: number) {
    const response = await fetch(`${this.baseUrl}/jobs/${jobId}/applications-zip/`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(error.error || error.detail || 'Download failed');
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : '';
    return { blob, filename };
  }

  // User management
  async getPendingUsers() {
    return this.requestAllPages<any>('/users/pending_approvals/');
  }

  async approveUser(id: number) {
    return this.request<any>(`/users/${id}/approve/`, {
      method: 'POST',
    });
  }

  async rejectUser(id: number) {
    return this.request<any>(`/users/${id}/reject/`, {
      method: 'POST',
    });
  }


  // Job approval management
  async getPendingJobs() {
    return this.requestAllPages<any>('/jobs/pending_approvals/');
  }

  async approveJob(id: number) {
    return this.request<any>(`/jobs/${id}/approve/`, {
      method: 'POST',
    });
  }

  async rejectJob(id: number) {
    return this.request<any>(`/jobs/${id}/reject/`, {
      method: 'POST',
    });
  }

  async updateApplicationStatus(id: number, status: string) {
    return this.request<any>(`/applications/${id}/update_status/`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
  }

  async updateApplicationNote(id: number, privateNote: string) {
    return this.request<any>(`/applications/${id}/update_note/`, {
      method: 'POST',
      body: JSON.stringify({ private_note: privateNote }),
    });
  }

  async withdrawApplication(id: number, confirmFinal = false) {
    return this.request<any>(`/applications/${id}/withdraw/`, {
      method: 'POST',
      body: JSON.stringify({ confirm_final: confirmFinal }),
    });
  }
}

export const api = new ApiService(API_BASE_URL);
export default api;
